import { supabase } from './supabaseService';
import { Organization } from '../types/multiTenant';

/**
 * Plan Features Definition
 */
export interface PlanFeatures {
  name: 'trial' | 'individual' | 'small_clinic' | 'clinic_pro' | 'enterprise';
  displayName: string;
  price: number;
  billingInterval: 'month' | 'year';
  features: string[];
  maxUsers: number | null; // null = unlimited
  maxClients: number | null; // null = unlimited
  color: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  popular?: boolean;
}

/**
 * Invoice Definition
 */
export interface Invoice {
  id: string;
  organizationId: string;
  invoiceNumber: string;
  amount: number;
  currency: string;
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  issueDate: string;
  dueDate: string;
  paidAt?: string;
  invoicePdf?: string;
  stripeInvoiceId?: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Available subscription plans
 */
export const PLANS: Record<string, PlanFeatures> = {
  trial: {
    name: 'trial',
    displayName: 'Teste',
    price: 0,
    billingInterval: 'month',
    features: [
      '30 dias de teste grátis',
      '1 usuário',
      '30 clientes',
      'Agenda básica',
      'Módulo financeiro',
      'Relatórios básicos',
      'Suporte por email',
    ],
    maxUsers: 1,
    maxClients: 30,
    color: 'inherit',
    // NOTA: Este plano só é acessível via chave de convite do admin
    // Não é exibido publicamente nas páginas de checkout/signup
  },
  individual: {
    name: 'individual',
    displayName: 'Individual',
    price: 19.9,
    billingInterval: 'month',
    features: [
      '1 usuário',
      'Pacientes ilimitados',
      'Agenda integrada completa',
      'Módulo financeiro',
      'Teleconsulta (videochamada)',
      'Fila de espera',
      'Dashboard de progresso',
      'Relatórios básicos',
      'Notificações por email',
      'Suporte por email',
    ],
    maxUsers: 1,
    maxClients: null,
    color: 'primary',
  },
  small_clinic: {
    name: 'small_clinic',
    displayName: 'Clínica',
    price: 49.9,
    billingInterval: 'month',
    features: [
      'Até 5 usuários',
      'Clientes ilimitados',
      'Agenda integrada completa',
      'Módulo financeiro completo',
      'Relatórios básicos e avançados',
      'Dashboard avançado',
      'Teleconsulta com videochamada',
      'Notas de sessão integradas',
      'Notificações Email + WhatsApp',
      'Gestão de equipe',
      'Suporte prioritário',
    ],
    maxUsers: 5,
    maxClients: null,
    color: 'success',
    popular: true,
  },
  clinic_pro: {
    name: 'clinic_pro',
    displayName: 'Clínica Pro',
    price: 99.9,
    billingInterval: 'month',
    features: [
      'Até 15 usuários',
      'Clientes ilimitados',
      'Teleconsulta com videochamada',
      '✨ Transcrição IA (100/mês)',
      '✨ Insights com IA',
      'Relatórios avançados',
      'Dashboard avançado',
      'Módulo financeiro completo',
      'Notificações Email + WhatsApp',
      'Gestão de equipe',
      'Programa de Indicação',
      'Suporte prioritário',
    ],
    maxUsers: 15,
    maxClients: null,
    color: 'warning',
  },
  enterprise: {
    name: 'enterprise',
    displayName: 'Enterprise',
    price: 199.9,
    billingInterval: 'month',
    features: [
      'Usuários ilimitados',
      'Clientes ilimitados',
      'Armazenamento ilimitado',
      'Teleconsulta com videochamada',
      '✨ Transcrição IA ilimitada',
      '✨ Insights com IA para sessões',
      '✨ Notas automáticas de consulta',
      'Relatórios básicos e avançados',
      'Dashboard avançado',
      'Módulo financeiro completo',
      'Notificações Email + WhatsApp + SMS',
      'Acesso completo à API',
      'Personalização de marca',
      'Suporte dedicado 24/7',
      'Onboarding personalizado',
    ],
    maxUsers: null,
    maxClients: null,
    color: 'secondary',
  },
};

/**
 * Public plans - planos visíveis publicamente (exclui trial)
 * O plano trial só é acessível via chave de convite do admin
 */
export const PUBLIC_PLANS: PlanFeatures[] = Object.values(PLANS).filter(
  plan => plan.name !== 'trial'
);

/**
 * Subscription Service
 * Handles all subscription-related operations
 */
class SubscriptionService {
  /**
   * Get all available plans (including trial)
   */
  getAvailablePlans(): PlanFeatures[] {
    return Object.values(PLANS);
  }

  /**
   * Get public plans (excludes trial - only accessible via invite key)
   */
  getPublicPlans(): PlanFeatures[] {
    return Object.values(PUBLIC_PLANS);
  }

  /**
   * Get plan by ID
   */
  getPlanById(planId: string): PlanFeatures | null {
    return PLANS[planId] || null;
  }

  /**
   * Change organization plan (upgrade/downgrade)
   */
  async changePlan(
    organizationId: string,
    newPlanId: 'trial' | 'individual' | 'small_clinic' | 'clinic_pro' | 'enterprise'
  ): Promise<Organization | null> {
    try {
      const newPlan = PLANS[newPlanId];
      if (!newPlan) {
        console.error('Invalid plan ID:', newPlanId);
        return null;
      }

      // Calculate new subscription dates
      const now = new Date();
      const subscriptionEndsAt = new Date(now);
      subscriptionEndsAt.setMonth(subscriptionEndsAt.getMonth() + 1); // 1 month from now

      // Determine subscription status
      let subscriptionStatus: Organization['subscriptionStatus'] = 'active';
      if (newPlanId === 'trial') {
        subscriptionStatus = 'trialing';
      }

      // Update organization in Supabase
      const { data, error } = await supabase
        .from('organizations')
        .update({
          plan_id: newPlanId,
          subscription_status: subscriptionStatus,
          subscription_ends_at: subscriptionEndsAt.toISOString(),
          max_users: newPlan.maxUsers,
          max_clients: newPlan.maxClients,
          updated_at: now.toISOString(),
        })
        .eq('id', organizationId)
        .select()
        .single();

      if (error) {
        console.error('Error changing plan:', error);
        return null;
      }

      // Transform and return
      return this.transformOrganization(data);
    } catch (error) {
      console.error('Error in changePlan:', error);
      return null;
    }
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription(organizationId: string, reason?: string): Promise<boolean> {
    try {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('organizations')
        .update({
          subscription_status: 'canceled',
          canceled_at: now,
          cancellation_reason: reason || null,
          updated_at: now,
        })
        .eq('id', organizationId);

      if (error) {
        console.error('Error canceling subscription:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in cancelSubscription:', error);
      return false;
    }
  }

  /**
   * Reactivate subscription
   */
  async reactivateSubscription(
    organizationId: string,
    planId?: 'individual' | 'small_clinic' | 'clinic_pro' | 'enterprise'
  ): Promise<Organization | null> {
    try {
      // If planId is provided, use it; otherwise keep current plan
      if (planId) {
        return await this.changePlan(organizationId, planId);
      }

      const { data, error } = await supabase
        .from('organizations')
        .update({
          subscription_status: 'active',
          updated_at: new Date().toISOString(),
        })
        .eq('id', organizationId)
        .select()
        .single();

      if (error) {
        console.error('Error reactivating subscription:', error);
        return null;
      }

      return this.transformOrganization(data);
    } catch (error) {
      console.error('Error in reactivateSubscription:', error);
      return null;
    }
  }

  /**
   * Check if organization can upgrade to a plan
   */
  canUpgradeTo(currentPlanId: string, targetPlanId: string): boolean {
    const planHierarchy = ['trial', 'individual', 'small_clinic', 'clinic_pro', 'enterprise'];
    const currentIndex = planHierarchy.indexOf(currentPlanId);
    const targetIndex = planHierarchy.indexOf(targetPlanId);

    return targetIndex > currentIndex;
  }

  /**
   * Check if organization can downgrade to a plan
   */
  canDowngradeTo(currentPlanId: string, targetPlanId: string): boolean {
    const planHierarchy = ['trial', 'individual', 'small_clinic', 'clinic_pro', 'enterprise'];
    const currentIndex = planHierarchy.indexOf(currentPlanId);
    const targetIndex = planHierarchy.indexOf(targetPlanId);

    return targetIndex < currentIndex;
  }

  /**
   * Calculate proration amount (upgrade/downgrade)
   */
  calculateProration(
    currentPlanId: string,
    newPlanId: string,
    daysRemaining: number
  ): number {
    const currentPlan = PLANS[currentPlanId];
    const newPlan = PLANS[newPlanId];

    if (!currentPlan || !newPlan) return 0;

    // Calculate daily rates
    const currentDailyRate = currentPlan.price / 30;
    const newDailyRate = newPlan.price / 30;

    // Credit for unused days on current plan
    const credit = currentDailyRate * daysRemaining;

    // Charge for remaining days on new plan
    const charge = newDailyRate * daysRemaining;

    return charge - credit;
  }

  /**
   * Request plan change - calculates if payment is required and amount
   * Returns details needed to proceed with upgrade/downgrade
   */
  requestPlanChange(
    currentPlanId: string,
    newPlanId: string,
    subscriptionEndsAt: string | null
  ): {
    requiresPayment: boolean;
    amount: number;
    type: 'upgrade' | 'downgrade' | 'lateral';
    daysRemaining: number;
    newPlanPrice: number;
    currentPlanPrice: number;
  } {
    const currentPlan = PLANS[currentPlanId];
    const newPlan = PLANS[newPlanId];

    if (!currentPlan || !newPlan) {
      return {
        requiresPayment: false,
        amount: 0,
        type: 'lateral',
        daysRemaining: 0,
        newPlanPrice: 0,
        currentPlanPrice: 0,
      };
    }

    // Calculate days remaining in current subscription
    let daysRemaining = 30; // Default to full month if no end date
    if (subscriptionEndsAt) {
      const endDate = new Date(subscriptionEndsAt);
      const now = new Date();
      daysRemaining = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    }

    // Determine change type
    const isUpgrade = this.canUpgradeTo(currentPlanId, newPlanId);
    const isDowngrade = this.canDowngradeTo(currentPlanId, newPlanId);
    const type: 'upgrade' | 'downgrade' | 'lateral' = isUpgrade ? 'upgrade' : isDowngrade ? 'downgrade' : 'lateral';

    // Calculate prorated amount
    const prorationAmount = this.calculateProration(currentPlanId, newPlanId, daysRemaining);

    // Upgrades require payment of the difference
    // Downgrades give credit (no immediate payment)
    // Lateral changes have no cost
    const requiresPayment = isUpgrade && prorationAmount > 0;
    const amount = Math.abs(Math.round(prorationAmount * 100) / 100); // Round to 2 decimal places

    return {
      requiresPayment,
      amount,
      type,
      daysRemaining,
      newPlanPrice: newPlan.price,
      currentPlanPrice: currentPlan.price,
    };
  }

  /**
   * Apply plan change after payment is confirmed (or for downgrades/free changes)
   * This should only be called after payment is confirmed for upgrades
   */
  async applyPlanChange(
    organizationId: string,
    newPlanId: 'trial' | 'individual' | 'small_clinic' | 'clinic_pro' | 'enterprise',
    paymentConfirmed: boolean = false
  ): Promise<Organization | null> {
    // Use existing changePlan but with validation
    return this.changePlan(organizationId, newPlanId);
  }

  /**
   * Get invoices for an organization
   */
  async getInvoices(organizationId: string): Promise<Invoice[]> {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('organization_id', organizationId)
        .order('invoice_date', { ascending: false });

      if (error) {
        // Silently return empty if table doesn't exist (common during development)
        if (error.code === '42P01' || error.message?.includes('relation') || error.code === 'PGRST204') {
          return [];
        }
        console.error('Error fetching invoices:', error);
        return [];
      }

      return (data || []).map(this.transformInvoice);
    } catch (error) {
      console.error('Error in getInvoices:', error);
      return [];
    }
  }

  /**
   * Get invoice by ID
   */
  async getInvoiceById(invoiceId: string): Promise<Invoice | null> {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('id', invoiceId)
        .single();

      if (error) {
        console.error('Error fetching invoice:', error);
        return null;
      }

      return this.transformInvoice(data);
    } catch (error) {
      console.error('Error in getInvoiceById:', error);
      return null;
    }
  }

  /**
   * Create invoice (for manual invoicing)
   */
  async createInvoice(data: {
    organizationId: string;
    amount: number;
    currency?: string;
    description?: string;
    dueDate?: string;
  }): Promise<Invoice | null> {
    try {
      const invoiceNumber = await this.generateInvoiceNumber(data.organizationId);
      const now = new Date();
      const dueDate = data.dueDate || new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString(); // 7 days

      const { data: invoiceData, error } = await supabase
        .from('invoices')
        .insert({
          organization_id: data.organizationId,
          invoice_number: invoiceNumber,
          amount: data.amount,
          currency: data.currency || 'BRL',
          status: 'open',
          invoice_date: now.toISOString(),
          due_date: dueDate,
          description: data.description,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating invoice:', error);
        return null;
      }

      return this.transformInvoice(invoiceData);
    } catch (error) {
      console.error('Error in createInvoice:', error);
      return null;
    }
  }

  /**
   * Mark invoice as paid
   */
  async markInvoiceAsPaid(invoiceId: string): Promise<Invoice | null> {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .update({
          status: 'paid',
          paid_at: new Date().toISOString(),
        })
        .eq('id', invoiceId)
        .select()
        .single();

      if (error) {
        console.error('Error marking invoice as paid:', error);
        return null;
      }

      return this.transformInvoice(data);
    } catch (error) {
      console.error('Error in markInvoiceAsPaid:', error);
      return null;
    }
  }

  /**
   * Generate invoice number
   */
  private async generateInvoiceNumber(organizationId: string): Promise<string> {
    try {
      // Get count of invoices for this organization
      const { count, error } = await supabase
        .from('invoices')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organizationId);

      if (error) {
        console.error('Error counting invoices:', error);
        return `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;
      }

      const year = new Date().getFullYear();
      const number = (count || 0) + 1;

      return `INV-${year}-${String(number).padStart(4, '0')}`;
    } catch (error) {
      console.error('Error in generateInvoiceNumber:', error);
      return `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;
    }
  }

  /**
   * Transform database organization to app format
   */
  private transformOrganization(data: any): Organization {
    return {
      id: data.id,
      name: data.name,
      slug: data.slug,
      businessType: data.business_type,
      logoUrl: data.logo_url,
      planId: data.plan_id,
      subscriptionStatus: data.subscription_status,
      trialEndsAt: data.trial_ends_at,
      subscriptionEndsAt: data.subscription_ends_at,
      config: data.config || {},
      maxUsers: data.max_users,
      maxClients: data.max_clients,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      deleted: data.deleted || false,
    };
  }

  /**
   * Transform database invoice to app format
   */
  private transformInvoice(data: any): Invoice {
    return {
      id: data.id,
      organizationId: data.organization_id,
      invoiceNumber: data.invoice_number,
      amount: data.amount,
      currency: data.currency,
      status: data.status,
      issueDate: data.invoice_date,
      dueDate: data.due_date,
      paidAt: data.paid_at,
      invoicePdf: data.invoice_pdf,
      stripeInvoiceId: data.stripe_invoice_id,
      description: data.description,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }

  /**
   * Get mock invoices (for development/demo)
   */
  getMockInvoices(organizationId: string): Invoice[] {
    const now = new Date();
    return [
      {
        id: '3',
        organizationId,
        invoiceNumber: 'INV-2025-003',
        amount: 49.9,
        currency: 'BRL',
        status: 'paid',
        issueDate: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        dueDate: new Date(now.getTime() + 6 * 24 * 60 * 60 * 1000).toISOString(),
        paidAt: new Date(now.getTime()).toISOString(),
        invoicePdf: undefined,
        description: 'Assinatura Small Clinic - Março 2025',
        createdAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(now.getTime()).toISOString(),
      },
      {
        id: '2',
        organizationId,
        invoiceNumber: 'INV-2025-002',
        amount: 49.9,
        currency: 'BRL',
        status: 'paid',
        issueDate: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        dueDate: new Date(now.getTime() - 23 * 24 * 60 * 60 * 1000).toISOString(),
        paidAt: new Date(now.getTime() - 25 * 24 * 60 * 60 * 1000).toISOString(),
        invoicePdf: undefined,
        description: 'Assinatura Small Clinic - Fevereiro 2025',
        createdAt: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(now.getTime() - 25 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: '1',
        organizationId,
        invoiceNumber: 'INV-2025-001',
        amount: 49.9,
        currency: 'BRL',
        status: 'paid',
        issueDate: new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000).toISOString(),
        dueDate: new Date(now.getTime() - 53 * 24 * 60 * 60 * 1000).toISOString(),
        paidAt: new Date(now.getTime() - 55 * 24 * 60 * 60 * 1000).toISOString(),
        invoicePdf: undefined,
        description: 'Assinatura Small Clinic - Janeiro 2025',
        createdAt: new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(now.getTime() - 55 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ];
  }
}

export const subscriptionService = new SubscriptionService();
export default subscriptionService;
